/**
 * Account + BYOK (api.md §12 — per-user, was project settings in v1).
 * The key renders as a hint only (first 7 + last 4); the full key never
 * comes back from the server. Logout revokes the session server-side and
 * lands on /login.
 *
 * Two pieces since 2026-09-03 (owner: "The API section should be separate"):
 * the profile, and the key as its own section, with a status pill and the
 * two actions on one row instead of a folded "Advanced" disclosure.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useDeleteApiKey, useLogout, useMe, usePutApiKey } from '../../api/queries';
import { Button, ErrorNote, FieldLabel, IconButton, PencilIcon, TextInput, TrashIcon } from '../../lib/ui';

export function AccountSection() {
  const navigate = useNavigate();
  const me = useMe();
  const logout = useLogout();

  if (me.isPending) return <p className="text-tertiary">Loading…</p>;
  if (me.isError) return <ErrorNote>{me.error.message}</ErrorNote>;

  const { user } = me.data;

  return (
    <div className="space-y-4" data-testid="account-section">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="m-0 text-primary">{user.display_name}</p>
          <p className="m-0 text-tertiary">{user.email}</p>
        </div>
        <Button
          kind="ghost"
          disabled={logout.isPending}
          onClick={() =>
            logout.mutate(undefined, {
              onSuccess: () => void navigate('/login', { replace: true }),
            })
          }
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}

/** The Anthropic key: its state, and the two things you can do to it. */
export function ApiKeySection() {
  const me = useMe();
  const putApiKey = usePutApiKey();
  // One save for the button and the Enter key (2026-09-05).
  const save = () =>
    putApiKey.mutate(draft.trim(), {
      onSuccess: () => {
        setDraft('');
        setEditing(false);
      },
    });
  const deleteApiKey = useDeleteApiKey();
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);

  if (me.isPending) return <p className="text-tertiary">Loading…</p>;
  if (me.isError) return <ErrorNote>{me.error.message}</ErrorNote>;

  const { api_key_present, api_key_hint } = me.data;

  return (
    <div className="space-y-4" data-testid="byok-section">
      {api_key_present && !editing ? (
        <div className="flex flex-wrap items-center gap-3 rounded-card border border-default bg-raised px-4 py-3">
          <span
            data-testid="byok-state"
            className="rounded-pill border border-(--positive) px-2 py-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-(--positive)"
          >
            Saved
          </span>
          <span className="mono min-w-0 flex-1 text-primary">{api_key_hint}</span>
          <div className="flex items-center gap-2">
            <IconButton label="Replace the key" onClick={() => setEditing(true)} testId="byok-replace">
              <PencilIcon />
            </IconButton>
            <IconButton
              kind="danger"
              label="Remove the key"
              disabled={deleteApiKey.isPending}
              onClick={() => deleteApiKey.mutate()}
              testId="byok-remove"
            >
              <TrashIcon />
            </IconButton>
          </div>
        </div>
      ) : (
        <div className="max-w-xl">
          {!api_key_present ? (
            <p className="mt-0 mb-3 text-secondary">
              <span
                data-testid="byok-state"
                className="mr-2 rounded-pill border border-default px-2 py-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
              >
                No key yet
              </span>
              Paste one from your Anthropic console. It is checked with a request that costs nothing.
            </p>
          ) : null}
          <FieldLabel>Anthropic API key</FieldLabel>
          <div className="flex gap-2">
            <TextInput
              value={draft}
              onChange={setDraft}
              placeholder="sk-ant-…"
              type="password"
              mono
              ariaLabel="Anthropic API key"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && draft.trim().length > 0 && !putApiKey.isPending) save();
              }}
            />
            <Button
              kind="primary"
              disabled={draft.trim().length === 0 || putApiKey.isPending}
              onClick={save}
            >
              {putApiKey.isPending ? 'Checking…' : 'Save'}
            </Button>
            {api_key_present ? (
              <Button kind="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            ) : null}
          </div>
          <ErrorNote>{putApiKey.isError ? putApiKey.error.message : null}</ErrorNote>
        </div>
      )}
      <ErrorNote>{deleteApiKey.isError ? deleteApiKey.error.message : null}</ErrorNote>
    </div>
  );
}
